import type {
  AuthResponse,
  User,
  Invoice,
  RateCard,
  HaulierLineItem,
  WarehouseLineItem,
  DashboardStats,
  UploadUrlResponse,
  ConsignmentLookupResult,
  ActivityQueryResult,
  ReportSummary,
  HaulierReportLine,
  ServiceSurcharge,
  Haulier,
  HaulierConfig,
  AnyHaulierRate,
  WarehouseRate,
} from "../types";

const BASE_URL = "/api";

// ─── HTTP Client ────────────────────────────────────────────────────────────

function getToken(): string | null {
  return localStorage.getItem("zr_token");
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message =
      (body as { message?: string; error?: string }).message ??
      (body as { error?: string }).error ??
      `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export const auth = {
  requestOtp(email: string): Promise<{ message: string }> {
    return request("/auth/request-otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  verifyOtp(email: string, code: string): Promise<AuthResponse> {
    return request("/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  },

  getMe(): Promise<{ user: User }> {
    return request("/auth/me");
  },
};

// ─── Invoices ───────────────────────────────────────────────────────────────

export const invoices = {
  list(lastKey?: string): Promise<{ invoices: Invoice[]; last_key?: string }> {
    const params = lastKey ? `?last_key=${encodeURIComponent(lastKey)}` : "";
    return request(`/invoices${params}`);
  },

  get(
    id: string,
  ): Promise<{
    invoice: Invoice;
    line_items: (HaulierLineItem | WarehouseLineItem)[];
  }> {
    return request(`/invoices/${encodeURIComponent(id)}`);
  },

  create(data: {
    file_key: string;
    file_hash: string;
    original_filename: string;
    content_type: string;
  }): Promise<{ invoice: Invoice }> {
    return request("/invoices", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  getUploadUrl(
    filename: string,
    content_type: string,
  ): Promise<UploadUrlResponse> {
    const params = new URLSearchParams({ filename, content_type });
    return request(`/upload-url?${params.toString()}`);
  },

  async downloadFile(id: string): Promise<void> {
    const { download_url } = await request<{ download_url: string }>(
      `/invoices/${encodeURIComponent(id)}/download`,
    );
    window.open(download_url, "_blank");
  },

  fuelCheck(id: string, dieselPence: number): Promise<Invoice> {
    return request(`/invoices/${encodeURIComponent(id)}/fuel-check`, {
      method: "POST",
      body: JSON.stringify({ diesel_pence: dieselPence }),
    });
  },
};

// ─── Rate Cards ─────────────────────────────────────────────────────────────

export const rateCards = {
  list(): Promise<{ rate_cards: RateCard[] }> {
    return request("/rate-cards");
  },

  get(
    id: string,
  ): Promise<{ rate_card: RateCard; rates: Record<string, unknown>[] }> {
    return request(`/rate-cards/${encodeURIComponent(id)}`);
  },

  getRates(
    id: string,
  ): Promise<{ rates: (AnyHaulierRate | WarehouseRate)[] }> {
    return request(`/rate-cards/${encodeURIComponent(id)}/rates`);
  },

  uploadHaulier(
    csv_content: string,
    effective_from: string,
    uploaded_by: string,
    haulier: Haulier,
  ): Promise<{ rate_card: RateCard }> {
    return request("/rate-cards/haulier", {
      method: "POST",
      body: JSON.stringify({ csv_content, effective_from, uploaded_by, haulier }),
    });
  },

  setWarehouse(
    rates: { charge_type: string; unit_price: number }[],
    effective_from: string,
    uploaded_by: string,
  ): Promise<{ rate_card: RateCard }> {
    return request("/rate-cards/warehouse", {
      method: "POST",
      body: JSON.stringify({ rates, effective_from, uploaded_by }),
    });
  },

  async download(id: string): Promise<void> {
    const { download_url } = await request<{ download_url: string }>(
      `/rate-cards/${encodeURIComponent(id)}/download`,
    );
    window.open(download_url, "_blank");
  },
};

// ─── Consignments ───────────────────────────────────────────────────────────

export const consignments = {
  lookup(number: string): Promise<ConsignmentLookupResult> {
    return request(
      `/consignments/${encodeURIComponent(number)}`,
    );
  },
};

// ─── Activity ───────────────────────────────────────────────────────────────

export const activity = {
  list(filters?: {
    user?: string;
    action_type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
  }): Promise<ActivityQueryResult> {
    const params = new URLSearchParams();
    if (filters?.user) params.set("user", filters.user);
    if (filters?.action_type) params.set("action_type", filters.action_type);
    if (filters?.date_from) params.set("date_from", filters.date_from);
    if (filters?.date_to) params.set("date_to", filters.date_to);
    if (filters?.page) params.set("page", String(filters.page));
    const qs = params.toString();
    return request(`/activity${qs ? `?${qs}` : ""}`);
  },
};

// ─── Dashboard ──────────────────────────────────────────────────────────────

export const dashboard = {
  getStats(): Promise<DashboardStats> {
    return request("/dashboard/stats");
  },
};

// ─── Config ─────────────────────────────────────────────────────────────────

export const config = {
  getSurcharges(haulier?: Haulier): Promise<{ surcharges: ServiceSurcharge[] }> {
    const params = haulier ? `?haulier=${encodeURIComponent(haulier)}` : "";
    return request(`/config/surcharges${params}`);
  },

  saveSurcharges(
    haulier: Haulier,
    surcharges: ServiceSurcharge[],
  ): Promise<{ surcharges: ServiceSurcharge[]; count: number }> {
    return request("/config/surcharges", {
      method: "PUT",
      body: JSON.stringify({ haulier, surcharges }),
    });
  },

  getHaulierConfig(haulier: Haulier): Promise<HaulierConfig> {
    return request(`/config/hauliers/${encodeURIComponent(haulier)}`);
  },

  saveHaulierConfig(
    haulier: Haulier,
    cfg: Omit<HaulierConfig, "haulier" | "updated_by" | "updated_at">,
  ): Promise<HaulierConfig> {
    return request(`/config/hauliers/${encodeURIComponent(haulier)}`, {
      method: "PUT",
      body: JSON.stringify(cfg),
    });
  },
};

// ─── Reports ─────────────────────────────────────────────────────────────────

export const reports = {
  summary(from: string, to: string): Promise<ReportSummary> {
    const params = new URLSearchParams({ from, to });
    return request(`/reports/summary?${params.toString()}`);
  },

  haulierLines(
    from: string,
    to: string,
  ): Promise<{ lines: HaulierReportLine[]; total_count: number }> {
    const params = new URLSearchParams({ from, to });
    return request(`/reports/haulier-lines?${params.toString()}`);
  },
};
